import { ArrowLeft, Save } from "lucide-react";
import { useId } from "react";

import type { PartProblem } from "@/lib/assetContent";
import type { NamedDocument } from "@/lib/namedParts";
import { Button } from "@/ui/Button";
import { Label, Textarea } from "@/ui/Input";
import { UnavailableState } from "@/ui/states";

/**
 * One named document, open for editing (#492).
 *
 * The canonical row editor and the canonical BG-set file editor are the same
 * screen with different words in it: a heading naming the document, one content
 * field, Back and Save. This is that screen, and it is deliberately
 * presentational and total — it owns no draft and performs no request — so the
 * same component renders the authoring surface and the read-only one, and
 * `readOnly` is one prop every control below reads. That is the structural form
 * of #33's rule, and it is why #493 can reuse this for pathway families without
 * inheriting a decision about who saves.
 *
 * ONE FIELD PER STORED DOCUMENT, NOT ONE PER ROLE. The canonical reference shows
 * exactly one content box because its mock data is monolingual. This
 * repository's assets are not: part keys are language-prefixed and the seeded
 * main concern carries five roles in Hebrew and two in English
 * (`services/editor-service/src/editor_service/seed/manifest.yaml:9-14`). A
 * single box would have to pick one language and silently hide the other, which
 * is worse than either alternative. So a role with one document renders exactly
 * the canonical single field, and a role with several renders one per language,
 * each labelled. The label is a READ-ONLY fact about which document is which —
 * never a control that authors a prefix, which the 2026-08-25 amendment forbids.
 *
 * ONLY PROMPT DOCUMENTS REACH THE WRITABLE FIELD. A structured document —
 * `steps`, `schema`, `diagnosis`, `preamble_config`, the folded `domains` — is
 * engine-owned: `StructuredPart.data` is an unconstrained `JsonValue`,
 * "engine-owned and validated by the authoritative loader/validator (#7/#16),
 * not re-specified here"
 * (`services/editor-service/src/editor_service/schemas/library.py:143-157`).
 * An earlier arrangement here rendered its bytes into a writable textarea,
 * which is a generic JSON editor by another name: it let a clinician retype the
 * inside of a document whose shape this repository deliberately does not own,
 * and publish it into content every adopting project inherits. #446's conflict
 * rule answers that case directly — where the canonical surface would require
 * an authority this repository does not hold, the implementation "must fail
 * closed or remain explicitly unavailable", which is the ruling `BgSetDomainRoute`
 * already applies to per-domain steps. So a structured document states why it
 * cannot be authored here, and its stored bytes and order pass through
 * untouched.
 */

/**
 * Back and Save, for the caller's `PageHeader` actions slot.
 *
 * THE DOCUMENT'S NAME IS THE PAGE'S HEADING, and there is exactly one of it.
 * An earlier arrangement here rendered the name again above the content field,
 * which put two headings with the same text on one screen — a duplicate landmark
 * for assistive technology, and not what the canonical reference does: its row
 * editor has one heading and its file editor has one heading. So the caller's
 * page header owns the name, the subtitle and the metadata strip, and this file
 * contributes the controls and the fields.
 */
export function NamedDocumentActions({
  readOnly,
  dirty,
  saving,
  onBack,
  onSave,
  backLabel = "Back",
}: {
  readOnly: boolean;
  dirty: boolean;
  saving: boolean;
  onBack: () => void;
  onSave: () => void;
  backLabel?: string;
}) {
  return (
    <>
      <Button variant="outline" onClick={onBack}>
        <ArrowLeft aria-hidden="true" className="h-4 w-4" />
        {backLabel}
      </Button>
      {readOnly ? null : (
        <Button disabled={!dirty || saving} onClick={onSave}>
          <Save aria-hidden="true" className="h-4 w-4" />
          Save
        </Button>
      )}
    </>
  );
}

/**
 * Whether a named row may be authored on this surface at all.
 *
 * Exported because the callers own the page CHROME — a screen whose only
 * document is engine-owned must not offer Save either, and a disabled Save with
 * nothing that could ever enable it is the dead control ADR 0001 forbids.
 */
export function isAuthorableDocumentSet(
  documents: readonly NamedDocument[],
): boolean {
  return documents.some((document) => document.kind === "prompt_text");
}

/**
 * The stated reason a structured document is not authored here.
 *
 * One component rather than a sentence repeated per surface, so the main-concern
 * row editor, the BG-set file editor and anything #493 adds all give a clinician
 * the same explanation for the same limit.
 */
export function StructuredDocumentUnavailable({
  count = 1,
}: {
  /** How many stored documents this covers, across languages. */
  count?: number;
}) {
  return (
    <UnavailableState
      title="This document is not authored in this editor"
      description={
        <>
          <p>
            Its contents are owned by the clinical engine rather than by this
            editor, and the shape they must take is not published here. Editing
            {count > 1 ? " these documents" : " it"} here would mean this editor
            deciding that shape and writing it into shared clinical content — so
            it states the limit instead of guessing at it.
          </p>
          <p className="mt-2">
            The document itself is unchanged and still part of this asset;
            nothing on this screen alters it.
          </p>
        </>
      }
      blockedBy="Waiting on the published document schema. Reconciliation is tracked by #448."
    />
  );
}

export function NamedDocumentEditor({
  documents,
  readOnly,
  readOnlyReason,
  problems,
  onChange,
}: {
  documents: readonly NamedDocument[];
  readOnly: boolean;
  readOnlyReason: string | null;
  problems: readonly PartProblem[];
  onChange: (documentId: string, text: string) => void;
}) {
  const listProblems = problems.filter((problem) => problem.index === null);
  // Partitioned rather than branched per field: a role stored in two languages
  // gets ONE explanation, not the same paragraph twice.
  const writable = documents.filter(
    (document) => document.kind === "prompt_text",
  );
  const engineOwned = documents.filter(
    (document) => document.kind !== "prompt_text",
  );

  return (
    <div className="space-y-4">
      {readOnly && readOnlyReason ? (
        <p className="rounded-md border border-border bg-muted px-3 py-2 text-sm text-muted-foreground">
          {readOnlyReason}
        </p>
      ) : null}

      {listProblems.length > 0 ? (
        // The live region is the WRAPPER, not each item. A `role` on an `<li>`
        // replaces its implicit `listitem`, which leaves the `<ul>` holding
        // children that are not list items — axe's `list` rule, and a screen
        // reader that stops announcing "list of 2" with it. Announcing the group
        // once is also the better behaviour: a save refused for three reasons
        // used to fire three assertive interruptions.
        <div role="alert">
          <ul className="space-y-1">
            {listProblems.map((problem, position) => (
              // Keyed on the position, not the sentence, which is the rule
              // `NamedDraftStates.NamedProblems` states in full: a message list
              // that is unique only by where its array came from is one prop
              // change away from handing React two children with one key. This
              // list filters to the set-level refusals, which are distinct
              // sentences today, and carries the rule anyway so the four copies
              // of this markup stay one idiom rather than four judgements.
              <li
                key={`${position}:${problem.index}:${problem.message}`}
                className="text-sm text-destructive"
              >
                {problem.message}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {engineOwned.length > 0 ? (
        <StructuredDocumentUnavailable count={engineOwned.length} />
      ) : null}

      <div className="space-y-4">
        {writable.map((document) => (
          <DocumentField
            key={document.id}
            document={document}
            showLanguage={writable.length > 1}
            readOnly={readOnly}
            onChange={(text) => onChange(document.id, text)}
          />
        ))}
      </div>
    </div>
  );
}

function DocumentField({
  document,
  showLanguage,
  readOnly,
  onChange,
}: {
  document: NamedDocument;
  showLanguage: boolean;
  readOnly: boolean;
  onChange: (text: string) => void;
}) {
  const fieldId = useId();

  return (
    <div className="space-y-1.5">
      {showLanguage ? (
        <Label htmlFor={fieldId}>
          {document.language === null
            ? "Every language"
            : `Language: ${document.language}`}
        </Label>
      ) : (
        <Label htmlFor={fieldId} className="sr-only">
          Document content
        </Label>
      )}
      <Textarea
        id={fieldId}
        rows={22}
        value={document.text}
        readOnly={readOnly}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}
